import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import VideoPlayer from './VideoPlayer';

try {
  pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;
} catch {
  // pdfjs worker init failed — PDFs won't render but app won't crash
}

interface FileViewerProps {
  url: string;
  type: 'pdf' | 'video';
  fileName?: string;
  className?: string;
}

const FileViewer: React.FC<FileViewerProps> = ({ url, type, fileName, className = '' }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [pdfError, setPdfError] = useState('');

  if (type === 'video') {
    return <VideoPlayer src={url} title={fileName} className={className} />;
  }

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {/* PDF Controls */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-surface border border-line rounded-lg w-full justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPageNumber(p => Math.max(1, p - 1))}
            disabled={pageNumber <= 1}
            className="px-3 py-1 bg-surface border border-line rounded text-text text-sm disabled:opacity-40 hover:bg-line transition-colors"
          >
            ← Prev
          </button>
          <span className="text-muted text-sm">
            Page {pageNumber} of {numPages}
          </span>
          <button
            onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
            disabled={pageNumber >= numPages}
            className="px-3 py-1 bg-surface border border-line rounded text-text text-sm disabled:opacity-40 hover:bg-line transition-colors"
          >
            Next →
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setScale(s => Math.max(0.5, +(s - 0.25).toFixed(2)))}
            className="px-2 py-1 bg-surface border border-line rounded text-text text-sm hover:bg-line transition-colors"
          >
            −
          </button>
          <span className="text-muted text-sm w-12 text-center">{Math.round(scale * 100)}%</span>
          <button
            onClick={() => setScale(s => Math.min(3, +(s + 0.25).toFixed(2)))}
            className="px-2 py-1 bg-surface border border-line rounded text-text text-sm hover:bg-line transition-colors"
          >
            +
          </button>
        </div>
      </div>

      {/* PDF Document */}
      <div className="overflow-auto max-h-[70vh] w-full flex justify-center">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {pdfError ? (
          <div className="text-danger text-sm py-8 text-center">{pdfError}</div>
        ) : (
          <Document
            file={url}
            onLoadSuccess={({ numPages: n }) => { setNumPages(n); setLoading(false); }}
            onLoadError={() => { setLoading(false); setPdfError('Failed to load PDF. The file may be unavailable or in an unsupported format.'); }}
            loading=""
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          </Document>
        )}
      </div>
    </div>
  );
};

export default FileViewer;
